import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react'
import * as RadixDialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '../utils'
import { Button } from './Button'

type DialogSize = 'sm' | 'md' | 'lg' | 'xl'

interface DialogProps {
  /** Os modais da app são montados condicionalmente, por isso o default é aberto. */
  open?: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  /** Ações no rodapé (alinhadas à direita). */
  footer?: ReactNode
  size?: DialogSize
  /** false impede fechar com Esc/clique fora — para quando há uma operação a decorrer. */
  dismissible?: boolean
  className?: string
}

const sizeStyles: Record<DialogSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
}

// Substitui os overlays `fixed inset-0` feitos à mão: focus trap, Esc,
// aria-labelledby/-describedby e fundo consistentes, via Radix.
export function Dialog({
  open = true,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
  className,
}: DialogProps) {
  const contentRef = useRef<HTMLDivElement>(null)
  return (
    <RadixDialog.Root open={open} onOpenChange={(next) => { if (!next && dismissible) onClose() }}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-fade-in" />
        <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4">
          <RadixDialog.Content
            className={cn(
              'pointer-events-auto flex max-h-[calc(100vh-2rem)] w-full flex-col rounded-2xl bg-surface-raised shadow-2xl animate-slide-in-up',
              'focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0',
              sizeStyles[size],
              className,
            )}
            ref={contentRef}
            {...(description ? {} : { 'aria-describedby': undefined })}
            // Um campo com autoFocus mantém o foco; sem isso, o foco vai para o
            // painel e não para o botão de fechar (que ficaria com o anel visível).
            onOpenAutoFocus={(e) => {
              e.preventDefault()
              const el = contentRef.current
              if (el && !el.contains(document.activeElement)) el.focus()
            }}
          >
            <div className="flex items-start justify-between gap-4 border-b border-line px-6 py-4">
              <div className="min-w-0">
                <RadixDialog.Title className="text-base font-semibold text-fg">{title}</RadixDialog.Title>
                {description && (
                  <RadixDialog.Description className="mt-0.5 text-xs text-fg-muted">
                    {description}
                  </RadixDialog.Description>
                )}
              </div>
              {dismissible && (
                <RadixDialog.Close
                  aria-label="Fechar"
                  className="rounded-lg p-1.5 text-fg-muted hover:bg-surface-sunken hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-600"
                >
                  <X className="h-4 w-4" />
                </RadixDialog.Close>
              )}
            </div>
            {children && <div className="overflow-y-auto p-6">{children}</div>}
            {footer && (
              <div className="flex justify-end gap-2 border-t border-line px-6 py-4">{footer}</div>
            )}
          </RadixDialog.Content>
        </div>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}

interface ConfirmOptions {
  title: ReactNode
  description?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** danger para ações destrutivas (eliminar, recusar, arquivar). */
  tone?: 'default' | 'danger'
}

interface ConfirmDialogProps extends ConfirmOptions {
  open?: boolean
  onClose: () => void
  onConfirm: () => void
  loading?: boolean
}

// Substitui window.confirm(): o título diz a ação e o objeto, a descrição
// diz a consequência ("Esta ação não pode ser revertida.").
export function ConfirmDialog({
  open = true,
  onClose,
  onConfirm,
  loading = false,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  tone = 'default',
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      size="sm"
      dismissible={!loading}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>{cancelLabel}</Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    />
  )
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | null>(null)

/**
 * Montar uma vez perto da raiz. Permite trocar `if (window.confirm(msg))`
 * por `if (await confirm({ title, description, tone: 'danger' }))` sem
 * estado local em cada ecrã.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null)
  const resolver = useRef<((ok: boolean) => void) | null>(null)

  const confirm = useCallback<ConfirmFn>((opts) => {
    // Um pedido novo enquanto outro está aberto resolve o anterior como cancelado.
    resolver.current?.(false)
    setOptions(opts)
    return new Promise<boolean>((resolve) => { resolver.current = resolve })
  }, [])

  const settle = (ok: boolean) => {
    resolver.current?.(ok)
    resolver.current = null
    setOptions(null)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {options && <ConfirmDialog {...options} onClose={() => settle(false)} onConfirm={() => settle(true)} />}
    </ConfirmContext.Provider>
  )
}

export function useConfirm(): ConfirmFn {
  const confirm = useContext(ConfirmContext)
  if (!confirm) throw new Error('useConfirm tem de ser usado dentro de <ConfirmProvider>')
  return confirm
}
